// Browser-side Monday.com service
// Uses proxiedFetch for CORS-safe API calls from the browser
// Monday uses a GraphQL API at https://api.monday.com/v2

import { proxiedFetch } from "./cors-proxy";
import type {
  MondayIntegrationSettings,
  MondayBoard,
  MondayItem,
  MondayActivityEvent,
} from "./types";

interface MondayConfig {
  apiKey?: string;
  /** OAuth2 Bearer access token — takes precedence over apiKey when provided */
  accessToken?: string;
}

export class BrowserMondayService {
  private authHeader: string;

  constructor(config: MondayConfig) {
    // OAuth2 Bearer token takes precedence over PAT
    if (config.accessToken?.trim()) {
      this.authHeader = `Bearer ${config.accessToken.trim()}`;
    } else {
      this.authHeader = (config.apiKey ?? '').trim();
    }
  }

  private async graphql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const res = await proxiedFetch("https://api.monday.com/v2", {
      method: "POST",
      headers: {
        Authorization: this.authHeader,
        "Content-Type": "application/json",
        "API-Version": "2024-10",
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!res.ok) {
      throw new Error(`Monday API error ${res.status}: ${res.statusText}`);
    }

    const json = await res.json();
    if (json.errors?.length) {
      throw new Error(`Monday GraphQL error: ${json.errors[0].message}`);
    }

    return json.data as T;
  }

  async getMe(): Promise<{ id: string; name: string; email: string; photo_thumb_small?: string }> {
    const data = await this.graphql<{
      me: { id: string; name: string; email: string; photo_thumb_small?: string };
    }>(`query { me { id name email photo_thumb_small } }`);
    return data.me;
  }

  async getBoards(limit: number = 25): Promise<MondayBoard[]> {
    const data = await this.graphql<{
      boards: RawMondayBoard[];
    }>(`query {
      boards(limit: ${limit}, order_by: used_at) {
        id
        name
        description
        state
        board_kind
        items_count
        workspace { id name }
      }
    }`);

    return data.boards.map((b) => ({
      id: b.id,
      name: b.name,
      description: b.description || undefined,
      state: b.state,
      boardKind: b.board_kind,
      itemsCount: b.items_count || 0,
      workspace: b.workspace || undefined,
    }));
  }

  async getBoardItems(
    boardId: string,
    opts: { limit?: number } = {}
  ): Promise<MondayItem[]> {
    const limit = opts.limit || 50;

    const data = await this.graphql<{
      boards: {
        items_page: { items: RawMondayItem[] };
      }[];
    }>(`query {
      boards(ids: [${boardId}]) {
        items_page(limit: ${limit}) {
          items {
            id
            name
            state
            group { title }
            created_at
            updated_at
            column_values { id title text type }
            creator { id name }
            subscribers { id name }
          }
        }
      }
    }`);

    const board = data.boards[0];
    if (!board) return [];

    return board.items_page.items.map((item) => ({
      id: item.id,
      name: item.name,
      state: item.state || "active",
      groupTitle: item.group?.title || "No Group",
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      columnValues: (item.column_values || []).map((cv) => ({
        id: cv.id,
        title: cv.title,
        text: cv.text || "",
        type: cv.type,
      })),
      creator: item.creator || undefined,
      subscribers: item.subscribers || undefined,
    }));
  }

  async getRecentActivity(
    boardId?: string,
    lookbackDays: number = 7
  ): Promise<MondayActivityEvent[]> {
    // Monday's activity_logs API requires board IDs
    if (!boardId) {
      // Fetch from all boards the user has access to
      const boards = await this.getBoards(10);
      const allEvents: MondayActivityEvent[] = [];
      for (const board of boards.slice(0, 5)) {
        const events = await this.getBoardActivity(board.id, board.name, lookbackDays);
        allEvents.push(...events);
      }
      allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      return allEvents;
    }

    const boards = await this.getBoards(25);
    const boardName = boards.find((b) => b.id === boardId)?.name || "Board";
    return this.getBoardActivity(boardId, boardName, lookbackDays);
  }

  private async getBoardActivity(
    boardId: string,
    boardName: string,
    lookbackDays: number
  ): Promise<MondayActivityEvent[]> {
    const since = new Date(Date.now() - lookbackDays * 86400000);

    const data = await this.graphql<{
      boards: {
        activity_logs: RawMondayActivity[];
      }[];
    }>(`query {
      boards(ids: [${boardId}]) {
        activity_logs(limit: 100) {
          id
          event
          data
          created_at
          user_id
        }
      }
    }`);

    const board = data.boards[0];
    if (!board) return [];

    const events: MondayActivityEvent[] = [];

    for (const log of board.activity_logs) {
      const logDate = new Date(log.created_at);
      if (logDate < since) continue;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let parsedData: any = {};
      try {
        parsedData = typeof log.data === "string" ? JSON.parse(log.data) : log.data;
      } catch { /* ignore */ }

      events.push({
        id: log.id,
        timestamp: log.created_at,
        type: log.event || "update",
        itemName: parsedData?.pulse_name || parsedData?.item_name || "Unknown Item",
        boardName,
        author: {
          displayName: parsedData?.user_name || `User ${log.user_id}`,
        },
        fieldName: parsedData?.column_title,
        fromValue: parsedData?.previous_value
          ? typeof parsedData.previous_value === "string"
            ? parsedData.previous_value
            : JSON.stringify(parsedData.previous_value)
          : undefined,
        toValue: parsedData?.value
          ? typeof parsedData.value === "string"
            ? parsedData.value
            : JSON.stringify(parsedData.value)
          : undefined,
      });
    }

    events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return events;
  }
}

// ==================== Raw Types ====================

interface RawMondayBoard {
  id: string;
  name: string;
  description?: string;
  state: string;
  board_kind: string;
  items_count?: number;
  workspace?: { id: string; name: string };
}

interface RawMondayItem {
  id: string;
  name: string;
  state?: string;
  group?: { title: string };
  created_at: string;
  updated_at: string;
  column_values?: { id: string; title: string; text?: string; type: string }[];
  creator?: { id: string; name: string };
  subscribers?: { id: string; name: string }[];
}

interface RawMondayActivity {
  id: string;
  event: string;
  data: string;
  created_at: string;
  user_id: string;
}

// ==================== Validation ====================

export async function validateMondayCredentials(
  settings: Pick<MondayIntegrationSettings, "apiKey"> & { accessToken?: string }
): Promise<{ valid: boolean; error?: string; user?: { id: string; name: string; email: string } }> {
  try {
    const svc = new BrowserMondayService({
      apiKey: settings.apiKey,
      accessToken: settings.accessToken,
    });
    const me = await svc.getMe();
    return { valid: true, user: me };
  } catch (err: unknown) {
    const e = err as Error;
    return { valid: false, error: e.message || "Monday validation failed" };
  }
}
